import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LicenseService } from '../../../shared/license-list/services/license.service';
import { LocalSettingsService } from '../../../../lib/services/local-settings.service';
import {
  LicenseOptionItem,
  LicenseOptionsResponse,
} from '../../../shared/license-list/models/license-override-list.model';
import { NihiiHelper } from '../../../shared/helpers/nihii.helper';
import { MsLightActivationDialogComponent } from './ms-light-activation-dialog.component';

@Component({
  selector: 'ms-ms-light',
  templateUrl: './ms-light.component.html',
  styleUrls: ['./ms-light.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MsLightComponent implements OnInit {
  public readonly searchTerm = signal<string>('');
  public readonly normalizedSearchTerm = computed<string>(() =>
    this.searchTerm().trim().toLowerCase(),
  );
  public readonly options = signal<LicenseOptionItem[]>([]);
  public readonly filteredOptions = computed<LicenseOptionItem[]>(() =>
    this.options().filter((item: LicenseOptionItem): boolean =>
      this.matchesSearch(item, this.normalizedSearchTerm()),
    ),
  );
  public readonly isLoading = signal<boolean>(false);

  constructor(
    private licenseService: LicenseService,
    private localSettingsService: LocalSettingsService,
    private dialog: MatDialog,
  ) {}

  public async ngOnInit(): Promise<void> {
    const token: string | null = this.localSettingsService.oauthIdToken;
    if (!token) {
      return;
    }

    this.isLoading.set(true);

    try {
      const response: LicenseOptionsResponse = await this.licenseService.loadOptionsList(token);
      this.options.set(
        response.options.filter(
          (option: LicenseOptionItem): boolean =>
            option.option === 'MS_LIGHT',
        ),
      );
    } finally {
      this.isLoading.set(false);
    }
  }

  public updateSearchTerm(term: string): void {
    this.searchTerm.set(term);
  }

  public async activateLicense(): Promise<void> {
    const dialogRef = this.dialog.open(MsLightActivationDialogComponent, {
      width: '560px',
    });

    dialogRef.afterClosed().subscribe((created: boolean | undefined): void => {
      if (created) {
        void this.ngOnInit();
      }
    });
  }

  public async deactivateLicense(nihii: string): Promise<void> {
    const token: string | null = this.localSettingsService.oauthIdToken;
    if (!token) {
      return;
    }

    await this.licenseService.deleteOption(token, nihii, 'MS_LIGHT');
    this.options.update((items: LicenseOptionItem[]): LicenseOptionItem[] =>
      items.filter((item: LicenseOptionItem): boolean => item.nihii !== nihii),
    );
  }

  private matchesSearch(item: LicenseOptionItem, filter: string): boolean {
    if (!filter) {
      return true;
    }

    const normalizedNihii: string = NihiiHelper.normalizeToNihii8(item.nihii);
    const normalizedFilter: string = NihiiHelper.normalizeToNihii8(filter);

    if (!normalizedFilter) {
      return item.nihii.toLowerCase().includes(filter);
    }

    return normalizedNihii.includes(normalizedFilter);
  }
}
